import {useCallback, useEffect, useMemo, useState} from 'react';

import {dictionaryTreeToTreeStructure} from 'src/components/features-section';
import {useLicenseDataModules} from 'src/hooks/dictionaries';
import {getDefaultLicenseDataModules} from 'src/openapi/admin/apis/GetDefaultLicenseDataModulesApi';
import {getLicenseDataAccessModules} from 'src/openapi/admin/apis/GetLicenseDataAccessModulesApi';
import {saveLicenseDataAccessModules} from 'src/openapi/admin/apis/SaveLicenseDataAccessModulesApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {LicenseType} from 'src/openapi/admin/models/LicenseType';

type Props = {
    licenseId?: number,
    licenseType?: LicenseType,
    disabled?: boolean,
};

export const useLicenseDataSets = ({licenseType, licenseId, disabled}: Props) => {
    const apiDispatch = useAppDispatch();

    const [loading, setLoading] = useState(true);
    const [defaultDataSets, setDefaultDataSet] = useState<Array<string> | null>(null);
    const [initialDataSets, setInitialDataSets] = useState<Array<string> | null>(null);
    const [dataSets, setDataSets] = useState<Array<string>>([]);
    const {licenseDataModules} = useLicenseDataModules();

    useEffect(() => {
        let loadedDefaultDataSets: Array<string> = [];

        const loadDataSets = async (licenseTypeCode: LicenseType) => {
            setLoading(true);
            const defaultDataSetsResponse = await apiDispatch(getDefaultLicenseDataModules({licenseTypeCode}));
            if (defaultDataSetsResponse.payload.success) {
                loadedDefaultDataSets = defaultDataSetsResponse.payload.data;
                setDefaultDataSet(loadedDefaultDataSets);
            }

            if (licenseId) {
                const initialDataSetsResponse = await apiDispatch(getLicenseDataAccessModules({licenseId}));
                if (initialDataSetsResponse.payload.success) {
                    setInitialDataSets(initialDataSetsResponse.payload.data);
                    setDataSets(initialDataSetsResponse.payload.data);
                }
            } else {
                setInitialDataSets(loadedDefaultDataSets);
                setDataSets(loadedDefaultDataSets);
            }
            setLoading(false);
        };

        if (licenseType) {
            loadDataSets(licenseType);
        }
    }, [licenseId, licenseType, apiDispatch]);

    const onSaveDataSets = useCallback(async (createdLicenseId = licenseId) => {
        let error: string | null = null;

        if (createdLicenseId) {
            const saveDataSetsResponse = await apiDispatch(saveLicenseDataAccessModules({
                licenseId: createdLicenseId,
                requestBody: dataSets,
            }));
            if (saveDataSetsResponse.payload.error) {
                error = getError({error: saveDataSetsResponse.payload.data, separator: ' '});
            } else {
                setInitialDataSets(dataSets);
            }
        }

        return error;
    }, [dataSets, apiDispatch, licenseId]);

    const onDiscardDataSets = useCallback(() => {
        if (initialDataSets) {
            setDataSets(initialDataSets);
        }
    }, [initialDataSets]);

    const onResetDataSets = useCallback(() => {
        if (defaultDataSets) {
            setDataSets(defaultDataSets);
        }
    }, [defaultDataSets]);

    const isDefaultDataSets = useMemo(() => (defaultDataSets
        ? isDeepEqual({prev: defaultDataSets, current: dataSets})
        : true
    ), [defaultDataSets, dataSets]);

    const isInitialDataSets = useMemo(() => (initialDataSets
        ? isDeepEqual({prev: initialDataSets, current: dataSets})
        : true
    ), [initialDataSets, dataSets]);

    const dataSetsTree = useMemo(() => {
        return dictionaryTreeToTreeStructure(licenseDataModules, !!disabled);
    }, [disabled, licenseDataModules]);

    return {
        loading,
        dataSets,
        setDataSets,
        dataSetsTree,
        isDefaultDataSets,
        isInitialDataSets,
        onSaveDataSets,
        onDiscardDataSets,
        onResetDataSets,
    };
};
